import React, { useRef, useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';

const VideoKYC = () => {
  const videoRef = useRef(null);
  const recordedVideoRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const streamRef = useRef(null);

  const [recordedChunks, setRecordedChunks] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isPreviewAvailable, setIsPreviewAvailable] = useState(false);
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [cameraFacingMode, setCameraFacingMode] = useState("user");
  const [timer, setTimer] = useState(30);
  const [leadData, setLeadData] = useState(null);
  const [refstr, setRefstr] = useState(""); // ✅ store refstr here

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  // ✅ Get refstr from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refstrValue = params.get("refstr");

    if (refstrValue) {
      setRefstr(refstrValue); // save raw refstr into state

      // Assuming refstr is base64 lead_id
      const lead_id = atob(refstrValue);
      fetch(`https://fundswalla.in/Api/TaskApi/getLeadDetails?lead_id=${lead_id}`)
        .then((res) => res.json())
        .then((data) => {
          // Current API: { status:1, data:[{ first_name, ... }] }
          // Legacy API: [{ first_name, ... }]
          const rows = Array.isArray(data)
            ? data
            : Array.isArray(data?.data)
              ? data.data
              : [];

          if (rows.length > 0) {
            setLeadData(rows[0]);
          }
        })
        .catch((err) => console.error("API Error:", err));
    }
  }, []);

  // ✅ Start camera on mount
  useEffect(() => {
    if (window.isSecureContext || window.location.hostname === 'localhost') {
      startCamera();
    }
  }, [cameraFacingMode]);

  // ✅ Timer countdown
  useEffect(() => {
    let interval;
    if (isRecording && timer > 0) {
      interval = setInterval(() => setTimer(prev => prev - 1), 1000);
    }
    if (timer === 0 && isRecording) {
      stopRecording();
    }
    return () => clearInterval(interval);
  }, [isRecording, timer]);

  const startCamera = async () => {
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: cameraFacingMode },
        audio: true,
      });

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.muted = true;
        videoRef.current.playsInline = true;

        const playPromise = videoRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            console.warn("Autoplay blocked, waiting for user interaction.");
          });
        }
      }

      setIsCameraOn(true);
    } catch (error) {
      console.error("Camera/mic access denied:", error);
      alert("❌ Please allow camera and microphone access.");
    }
  };

  const startRecording = () => {
    if (!streamRef.current) {
      alert("Camera not ready.");
      return;
    }

    const mimeType = 'video/webm;codecs=vp8,opus';
    const options = MediaRecorder.isTypeSupported(mimeType) ? { mimeType } : {};
    const mediaRecorder = new MediaRecorder(streamRef.current, options);

    mediaRecorderRef.current = mediaRecorder;
    setRecordedChunks([]);
    setTimer(30);
    setIsRecording(true);
    setIsPreviewAvailable(false);

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        setRecordedChunks(prev => [...prev, event.data]);
      }
    };

    mediaRecorder.onstop = () => {
      setIsPreviewAvailable(true);
    };

    mediaRecorder.start();
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  };

  const retakeRecording = async () => {
    if (recordedVideoRef.current) {
      recordedVideoRef.current.pause();
      recordedVideoRef.current.removeAttribute("src");
      recordedVideoRef.current.load();
    }

    setRecordedChunks([]);
    setIsPreviewAvailable(false);
    setTimer(15);

    await startCamera();
    startRecording();
  };

const submitVideo = async () => {
  try {
    const blob = new Blob(recordedChunks, { type: 'video/webm' });
    const formData = new FormData();
    formData.append('video', blob, 'kyc-video.webm'); 
    formData.append('refstr', refstr); 

    const response = await fetch(
      "https://fundswalla.in/Api/TaskApi/callAwsforUploadVideo",
      {
        method: "POST",
        body: formData,
      }
    );

    const result = await response.json().catch(() => null);

    if (response.ok) {
      alert("✅ Video uploaded successfully!");
      console.log("Upload Response:", result || response.statusText);
    } else {
      alert("❌ Upload failed. Please try again.");
      console.error("Upload Error:", result || response.statusText);
    }
  } catch (error) {
    console.error("Upload Exception:", error);
    alert("⚠️ Something went wrong while uploading.");
  }
};

  const switchCamera = () => {
    setCameraFacingMode(prev => (prev === "user" ? "environment" : "user"));
  };

  const handleStartClick = () => {
    if (!isCameraOn) {
      startCamera();
    } else {
      startRecording();
    }
  };

  // ✅ Build dynamic script text
  const fullName = leadData
    ? [leadData.first_name, leadData.middle_name, leadData.sur_name]
        .map((part) => String(part || "").trim())
        .filter(Boolean)
        .join(" ")
    : "";

  const scriptText = leadData
    ? `My name is ${fullName || "N/A"}. I am taking a loan from Fundswalle of Rs ${leadData.loan_recommended || "N/A"} which I have to pay on or before ${leadData.repayment_date ? new Date(leadData.repayment_date).toLocaleDateString("en-GB") : "N/A"} and the repayment amount is ${leadData.repayment_amount || "N/A"}. If I am unable to pay, I will pay the applicable late payment charges.`
    : `My name is Rahul Sharma. I am doing this video KYC for loan verification with ABC Finance.`;

  return (
    <div className="container mt-5 p-4 mb-5 rounded shadow"
      style={{
        background: 'linear-gradient(135deg, #ff7e5f, #feb47b)',
        color: 'black',
        fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
        boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
        maxWidth: '700px'
      }}
    >
      {/* ✅ Hidden field with refstr value */}
      <input type="hidden" name="refstr" value={refstr} />

      {/* Live Camera Feed */}
      {!isPreviewAvailable && (
        <div className="text-center mt-3">
          <div className="ratio ratio-4x3 rounded overflow-hidden">
            <video
              ref={videoRef}
              autoPlay
              muted
              playsInline
              style={{
                objectFit: 'cover',
                width: '100%',
                height: '100%',
                backgroundColor: '#000'
              }}
            />
          </div>

          <div className="d-flex flex-column flex-md-row justify-content-center align-items-center gap-3 mt-3">
            {!isRecording && (
              <>
                <button
                  className="btn btn-dark btn-lg"
                  onClick={handleStartClick}
                  style={{ fontWeight: '600', letterSpacing: '1px' }}
                >
                  🎥 Start Recording
                </button>

                {isMobile && (
                  <button
                    className="btn btn-secondary btn-lg"
                    onClick={switchCamera}
                    style={{ fontWeight: '600', letterSpacing: '1px' }}
                  >
                    🔄 Switch Camera
                  </button>
                )}
              </>
            )}

            {isRecording && (
              <button
                className="btn btn-danger btn-lg px-4 py-2"
                onClick={stopRecording}
                style={{ fontWeight: '600', letterSpacing: '1px' }}
              >
                ⏹ Stop Recording
              </button>
            )}
          </div>

          {isRecording && (
            <p className="mt-3 text-white fw-bold fs-5">
              ⏱️ Recording... {timer}s left
            </p>
          )}
        </div>
      )}

      {/* Script Box — shown only if not previewing */}
{!isPreviewAvailable && (
  <div className="mt-4">
    <h4 style={{ color: 'white', fontWeight: '600' }}>
      📝 Please read the following script:
    </h4>
    <div className="alert text-white" style={{
      backgroundColor: 'black',
      fontSize: '1.1rem',
      borderRadius: '10px',
      border: '2px solid #feb47b',
      userSelect: 'none'
    }}>
      {scriptText}
    </div>
  </div>
)}


      {/* Video Preview */}
      {isPreviewAvailable && (
        <div className="text-center mt-4">
          <div className="ratio ratio-4x3 rounded overflow-hidden">
            <video
              ref={recordedVideoRef}
              controls
              src={URL.createObjectURL(new Blob(recordedChunks, { type: 'video/webm' }))}
              style={{
                objectFit: 'cover',
                width: '100%',
                height: '100%',
                backgroundColor: '#000'
              }}
            />
          </div>

          <div className="mt-3 d-flex flex-column flex-md-row justify-content-center gap-3">
            <button
              className="btn btn-warning btn-lg"
              onClick={retakeRecording}
              style={{ letterSpacing: '1px', fontWeight: '600' }}
            >
              🔁 Retake
            </button>
            <button
              className="btn btn-success btn-lg"
              onClick={submitVideo}
              style={{ letterSpacing: '1px', fontWeight: '600' }}
            >
              📤 Submit Video
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoKYC;
